import * as React from 'react'
import { fetchVehicleBatch } from '../api/fetchSoldVehicles'
import { JUSTE_ENCHERE_CONFIG, createDefaultCriteria } from '../config'
import { readStoredStats, updateStoredStats } from '../storage'
import { SoldVehicleSearchCriteria } from '../types'
import useGameSounds from './useGameSounds'
import type {
  GamePhase,
  GuessFeedback,
  GuessDirection,
  GuessEntry,
  QualificationChoice,
  QualificationHistoryEntry,
  SoldVehicle,
  StoredStats,
} from '../types'

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
type UseJusteEnchereOptions = {
  brand?: string | null
}

const buildConfigWithBrand = (brand: string | null) => {
  const baseCriteria: SoldVehicleSearchCriteria = createDefaultCriteria()
  const baseMust = baseCriteria.must ?? []
  const sanitizedMust = baseMust.filter((item) => item.key !== 'VehicleDetail.Make')
  const brandFilter = brand ? [{ key: 'VehicleDetail.Make', value: brand }] : []

  return {
    ...JUSTE_ENCHERE_CONFIG,
    fetch: {
      ...JUSTE_ENCHERE_CONFIG.fetch,
      criteria: {
        ...baseCriteria,
        must: [...sanitizedMust, ...brandFilter],
      },
    },
  }
}

const shuffleArray = <T,>(items: Array<T>) => {
  const copy = items.slice()
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

export const extractVehiclePrice = (vehicle?: SoldVehicle | null): number | null => {
  if (!vehicle) return null
  const adjudication = vehicle.priceIncVatExcFeesExcAdminFees
  if (typeof adjudication === 'number' && Number.isFinite(adjudication)) return adjudication
  const fallback = vehicle.priceIncVatIncFeesExcAdminFees
  if (typeof fallback === 'number' && Number.isFinite(fallback)) return fallback
  return null
}

const pickLabel = (vehicle?: SoldVehicle | null) => {
  if (!vehicle) return 'Véhicule mystère'
  const parts = [vehicle.make, vehicle.model, vehicle.version].filter(Boolean)
  if (parts.length === 0) return 'Véhicule mystère'
  return parts.join(' ')
}

type QualificationState = {
  vehicle: SoldVehicle | null
  referencePrice: number | null
  remainingMs: number
  successes: number
  history: Array<QualificationHistoryEntry>
}

type FinalRoundState = {
  vehicle: SoldVehicle | null
  remainingMs: number
  guesses: Array<GuessEntry>
}

const createQualificationState = (duration: number): QualificationState => ({
  vehicle: null,
  referencePrice: null,
  remainingMs: duration,
  successes: 0,
  history: [],
})

const createFinalState = (duration: number): FinalRoundState => ({
  vehicle: null,
  remainingMs: duration,
  guesses: [],
})

const hasValidPrice = (vehicle?: SoldVehicle | null) =>
  typeof extractVehiclePrice(vehicle) === 'number'

const resolveGuessDirection = (guess: number, target: number): GuessDirection => {
  if (guess === target) return 'correct'
  return guess < target ? 'higher' : 'lower'
}

export type JusteEnchereGameHook = {
  phase: GamePhase
  loading: boolean
  statusMessage: string
  error: string | null
  qualification: QualificationState
  finalRound: FinalRoundState
  stats: StoredStats
  lastDirection: GuessDirection | null
  qualificationFeedback: GuessFeedback | null
  finalFeedback: GuessFeedback | null
  finalCountdown: number | null
  startGame: () => Promise<void>
  submitQualificationGuess: (choice: QualificationChoice) => Promise<void>
  submitFinalGuess: (value: number) => void
  resetGame: () => void
  beginFinalCountdown: () => void
}

export const useJusteEnchereGame = (options: UseJusteEnchereOptions = {}): JusteEnchereGameHook => {
  const brandFilter = options.brand ? options.brand.trim() : null
  const config = React.useMemo(() => buildConfigWithBrand(brandFilter ?? null), [brandFilter])

  const [phase, setPhase] = React.useState<GamePhase>('idle')
  const [loading, setLoading] = React.useState(false)
  const [statusMessage, setStatusMessage] = React.useState(
    'Cliquez sur “Démarrer” pour lancer une nouvelle partie',
  )
  const [error, setError] = React.useState<string | null>(null)
  const [qualification, setQualification] = React.useState<QualificationState>(() =>
    createQualificationState(config.qualification.totalDurationMs),
  )
  const [finalRound, setFinalRound] = React.useState<FinalRoundState>(() =>
    createFinalState(config.final.durationMs),
  )
  const [stats, setStats] = React.useState<StoredStats>(() => readStoredStats())
  const [lastDirection, setLastDirection] = React.useState<GuessDirection | null>(null)
  const [qualificationFeedback, setQualificationFeedback] = React.useState<GuessFeedback | null>(
    null,
  )
  const [finalFeedback, setFinalFeedback] = React.useState<GuessFeedback | null>(null)
  const [finalCountdown, setFinalCountdown] = React.useState<number | null>(null)
  const { playAnswerClick, playCorrectAnswer, playWrongAnswer, playReveal } = useGameSounds()

  const vehiclesQueueRef = React.useRef<Array<SoldVehicle>>([])
  const usedVehicleIdsRef = React.useRef<Set<string>>(new Set())
  const fetchingRef = React.useRef(false)
  const qualificationDeadlineRef = React.useRef<number>(0)
  const finalDeadlineRef = React.useRef<number>(0)
  const pendingFinalVehicleRef = React.useRef<SoldVehicle | null>(null)

  React.useEffect(() => {
    vehiclesQueueRef.current.length = 0
    usedVehicleIdsRef.current.clear()
    pendingFinalVehicleRef.current = null
    setFinalCountdown(null)
  }, [config.fetch.criteria])

  const handleVehicleBatchFailure = React.useCallback((err: unknown) => {
    const defaultMessage = 'Impossible de récupérer des véhicules pour continuer la partie'
    const message = err instanceof Error && err.message ? err.message : defaultMessage
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('JusteEnchere fetchVehicleBatch error', err)
    }
    setError(message)
    setStatusMessage('Une erreur réseau a interrompu la partie. Veuillez réessayer plus tard.')
    setPhase('fail')
  }, [])

  const persistStats = React.useCallback((updater: (prev: StoredStats) => StoredStats) => {
    const next = updateStoredStats(updater)
    setStats(next)
  }, [])

  const fetchBatch = React.useCallback(async () => {
    if (fetchingRef.current) return
    fetchingRef.current = true
    try {
      const batch = await fetchVehicleBatch(config.fetch.criteria)
      const valid = batch.filter((v) => hasValidPrice(v))
      const used = usedVehicleIdsRef.current
      const existingIds = new Set(vehiclesQueueRef.current.map((v) => v.id))
      const incoming = valid.filter((v) => !used.has(v.id) && !existingIds.has(v.id))

      const randomized = shuffleArray(incoming)
      vehiclesQueueRef.current.push(...randomized)
    } finally {
      fetchingRef.current = false
    }
  }, [config.fetch.criteria])

  const ensureQueue = React.useCallback(async () => {
    if (vehiclesQueueRef.current.length === 0) {
      await fetchBatch()
    }
  }, [fetchBatch])

  const clampPrice = React.useCallback(
    (value: number) => clamp(value, config.priceBounds.min, config.priceBounds.max),
    [config.priceBounds.max, config.priceBounds.min],
  )

  const computeReferencePrice = React.useCallback(
    (vehicle: SoldVehicle) => {
      const target = extractVehiclePrice(vehicle) ?? 10_000
      const { min, max } = config.qualification.referenceOffsetRatio
      const ratio = min + Math.random() * (max - min)
      const direction = Math.random() > 0.5 ? 1 : -1
      const tentative = Math.round(target + target * ratio * direction)
      return clampPrice(tentative === target ? tentative + 500 * direction : tentative)
    },
    [clampPrice, config.qualification.referenceOffsetRatio],
  )

  const takeVehicleFromQueue = React.useCallback(
    async (mode: 'fifo' | 'random' = 'fifo') => {
      try {
        await ensureQueue()
      } catch (err) {
        handleVehicleBatchFailure(err)
        return null
      }
      const pick = () => {
        if (vehiclesQueueRef.current.length === 0) return undefined
        if (mode === 'random') {
          const index = Math.floor(Math.random() * vehiclesQueueRef.current.length)
          const [selected] = vehiclesQueueRef.current.splice(index, 1)
          return selected
        }
        return vehiclesQueueRef.current.shift()
      }
      const vehicle: SoldVehicle | undefined = pick()
      if (vehicle) return vehicle
      try {
        await fetchBatch()
      } catch (err) {
        handleVehicleBatchFailure(err)
        return null
      }
      return pick() ?? null
    },
    [ensureQueue, fetchBatch, handleVehicleBatchFailure],
  )

  const startQualificationRound = React.useCallback(
    async (successes: number) => {
      setLoading(true)
      try {
      const vehicle = await takeVehicleFromQueue()
      if (!vehicle || !hasValidPrice(vehicle)) {
        setError((prev) => prev ?? 'Impossible de récupérer un véhicule pour ce tour')
        setPhase('fail')
        return false
      }
      const referencePrice = computeReferencePrice(vehicle)
      const remainingMs = Math.max(0, qualificationDeadlineRef.current - Date.now())
      usedVehicleIdsRef.current.add(vehicle.id)
      setQualification((prev) => ({
          ...prev,
          successes,
          vehicle,
          referencePrice,
          remainingMs,
        }))
        setLastDirection(null)
        setStatusMessage('')
        setPhase('qualification')
        return true
      } finally {
        setLoading(false)
      }
    },
    [computeReferencePrice, takeVehicleFromQueue],
  )

  const startFinalRound = React.useCallback(
    async (preparedVehicle?: SoldVehicle | null) => {
      setLoading(true)
      try {
        let vehicle = preparedVehicle ?? pendingFinalVehicleRef.current
        if (!vehicle) {
          vehicle = await takeVehicleFromQueue('random')
          if (vehicle) {
            usedVehicleIdsRef.current.add(vehicle.id)
          }
        }
        if (!vehicle || !hasValidPrice(vehicle)) {
          setError((prev) => prev ?? 'Impossible de démarrer la finale, pas de véhicule disponible')
          setPhase('fail')
          return false
        }
        pendingFinalVehicleRef.current = null
        finalDeadlineRef.current = Date.now() + config.final.durationMs
        setFinalRound({ ...createFinalState(config.final.durationMs), vehicle })
        setLastDirection(null)
        setQualificationFeedback(null)
        setFinalFeedback(null)
        setStatusMessage('Trouve le juste prix en moins d’une minute !')
        setFinalCountdown(null)
        setPhase('final')
        return true
      } finally {
        setLoading(false)
      }
    },
    [config.final.durationMs, takeVehicleFromQueue],
  )

  const startGame = React.useCallback(async () => {
    setPhase('loading')
    setLoading(true)
    setError(null)
    setStatusMessage('Préparation de nouvelles enchères...')
    setQualification(createQualificationState(config.qualification.totalDurationMs))
    setFinalRound(createFinalState(config.final.durationMs))
    setQualificationFeedback(null)
    setFinalFeedback(null)
    setFinalCountdown(null)
    pendingFinalVehicleRef.current = null
    usedVehicleIdsRef.current.clear()
    persistStats((prev) => ({ ...prev, totalRuns: prev.totalRuns + 1 }))
    qualificationDeadlineRef.current = Date.now() + config.qualification.totalDurationMs
    try {
      vehiclesQueueRef.current.length = 0
      await fetchBatch()
      if (vehiclesQueueRef.current.length === 0) {
        throw new Error('Aucun véhicule disponible')
      }
      await startQualificationRound(0)
    } catch (err) {
      setError((err as Error).message)
      setPhase('fail')
    } finally {
      setLoading(false)
    }
  }, [
    config.final.durationMs,
    config.qualification.totalDurationMs,
    fetchBatch,
    persistStats,
    startQualificationRound,
  ])

  const prepareFinalRound = React.useCallback(async () => {
    setLoading(true)
    try {
      const vehicle = await takeVehicleFromQueue('random')
      if (!vehicle || !hasValidPrice(vehicle)) {
        setError((prev) => prev ?? 'Impossible de préparer la finale, pas de véhicule disponible')
        setPhase('fail')
        return false
      }
      usedVehicleIdsRef.current.add(vehicle.id)
      pendingFinalVehicleRef.current = vehicle
      setFinalRound({ ...createFinalState(config.final.durationMs), vehicle })
      setLastDirection(null)
      setQualificationFeedback(null)
      setFinalFeedback(null)
      setStatusMessage('')
      setFinalCountdown(null)
      setPhase('final_ready')
      return true
    } finally {
      setLoading(false)
    }
  }, [config.final.durationMs, takeVehicleFromQueue])

  const beginFinalCountdown = React.useCallback(() => {
    if (phase !== 'final_ready') return
    if (finalCountdown != null) return
    setFinalCountdown(3)
    setStatusMessage('La finale débute dans quelques secondes...')
  }, [finalCountdown, phase])

  const submitQualificationGuess = React.useCallback(
    async (choice: QualificationChoice) => {
      if (phase !== 'qualification') return
      const { vehicle, referencePrice, successes } = qualification
      if (!vehicle || referencePrice == null) return
      const tfc = extractVehiclePrice(vehicle)
      if (typeof tfc !== 'number') return
      const correctChoice: QualificationChoice = tfc > referencePrice ? 'higher' : 'lower'
      const historyEntry: QualificationHistoryEntry = {
        id: `${vehicle.id}-${Date.now()}`,
        choice,
        correctChoice,
        timestamp: Date.now(),
      }
      setQualification((prev) => ({
        ...prev,
        history: [historyEntry, ...prev.history].slice(0, 3),
      }))
      playAnswerClick()
      if (choice === correctChoice) {
        const updatedSuccesses = successes + 1
        setQualificationFeedback({
          id: historyEntry.id,
          type: 'success',
          message: 'Bonne réponse !',
        })
        playCorrectAnswer()
        setStatusMessage('Bravo ! Bonne intuition.')
        persistStats((prev) =>
          prev.bestQualificationStreak >= updatedSuccesses
            ? prev
            : { ...prev, bestQualificationStreak: updatedSuccesses },
        )
        if (updatedSuccesses >= config.qualification.requiredSuccesses) {
          setQualificationFeedback(null)
          await prepareFinalRound()
          return
        }
        await startQualificationRound(updatedSuccesses)
      } else {
        setQualificationFeedback({
          id: historyEntry.id,
          type: 'error',
          message: correctChoice === 'higher' ? 'C’était plus' : 'C’était moins',
        })
        playWrongAnswer()
        setStatusMessage('Raté ! C’était l’autre option.')
        await startQualificationRound(successes)
      }
    },
    [
      config.qualification.requiredSuccesses,
      phase,
      qualification,
      persistStats,
      prepareFinalRound,
      startQualificationRound,
    ],
  )

  const submitFinalGuess = React.useCallback(
    (rawValue: number) => {
      if (phase !== 'final') return
      const { vehicle } = finalRound
      if (!vehicle || !hasValidPrice(vehicle)) return
      const guess = clampPrice(Math.round(rawValue))
      const target = extractVehiclePrice(vehicle)
      if (typeof target !== 'number') return
      const direction = resolveGuessDirection(guess, target)
      playAnswerClick()
      const entry: GuessEntry = {
        id: `${vehicle.id}-${Date.now()}`,
        value: guess,
        direction,
        delta: Math.abs(target - guess),
      }
      setFinalRound((prev) => ({
        ...prev,
        guesses: [entry, ...prev.guesses].slice(0, config.final.maxHistory),
      }))
      setLastDirection(direction)
      let feedbackMessage = 'Juste prix trouvé !'
      if (direction === 'higher') feedbackMessage = 'C’est plus haut !'
      else if (direction === 'lower') feedbackMessage = 'C’est plus bas !'
      setFinalFeedback({
        id: entry.id,
        type: direction === 'correct' ? 'success' : 'error',
        message: feedbackMessage,
      })

      if (direction === 'correct') {
        const remaining = Math.max(0, finalDeadlineRef.current - Date.now())
        const elapsed = config.final.durationMs - remaining
        setStatusMessage('Juste prix trouvé !')
        setPhase('success')
        playCorrectAnswer()
        playReveal()
        persistStats((prev) => {
          const bestFinalTime =
            prev.bestFinalTimeMs == null ? elapsed : Math.min(prev.bestFinalTimeMs, elapsed)
          return {
            ...prev,
            bestFinalTimeMs: bestFinalTime,
            lastVehicleLabel: pickLabel(vehicle),
            lastVehiclePrice: target,
          }
        })
      } else {
        playWrongAnswer()
        setStatusMessage(direction === 'higher' ? 'C’est plus haut !' : 'C’est plus bas !')
      }
    },
    [clampPrice, config.final.durationMs, config.final.maxHistory, finalRound, persistStats, phase],
  )

  const resetGame = React.useCallback(() => {
    setPhase('idle')
    setError(null)
    setStatusMessage('Cliquez sur “Démarrer” pour relancer une partie')
    setQualification(createQualificationState(config.qualification.totalDurationMs))
    setFinalRound(createFinalState(config.final.durationMs))
    setLastDirection(null)
    setLoading(false)
    setQualificationFeedback(null)
    setFinalFeedback(null)
    setFinalCountdown(null)
    usedVehicleIdsRef.current.clear()
    vehiclesQueueRef.current.length = 0
    pendingFinalVehicleRef.current = null
  }, [config.final.durationMs, config.qualification.totalDurationMs])

  React.useEffect(() => {
    if (phase !== 'qualification') return undefined
    const tick = () => {
      const remaining = Math.max(0, qualificationDeadlineRef.current - Date.now())
      setQualification((prev) => ({ ...prev, remainingMs: remaining }))
      if (remaining === 0) {
        setStatusMessage('Temps écoulé !')
        setPhase('fail')
      }
    }
    const id = window.setInterval(tick, 150)
    tick()
    return () => window.clearInterval(id)
  }, [phase])

  React.useEffect(() => {
    if (phase !== 'final') return undefined
    const tick = () => {
      const remaining = Math.max(0, finalDeadlineRef.current - Date.now())
      setFinalRound((prev) => ({ ...prev, remainingMs: remaining }))
      if (remaining === 0) {
        setStatusMessage('Temps écoulé !')
        setPhase('fail')
      }
    }
    const id = window.setInterval(tick, 150)
    tick()
    return () => window.clearInterval(id)
  }, [phase])

  React.useEffect(() => {
    if (phase !== 'final_ready') return undefined
    if (finalCountdown == null) return undefined
    if (finalCountdown === 0) {
      startFinalRound(pendingFinalVehicleRef.current)
      return undefined
    }
    const timer = window.setTimeout(() => {
      setFinalCountdown((prev) => (prev == null ? null : prev - 1))
    }, 1000)
    return () => window.clearTimeout(timer)
  }, [finalCountdown, phase, startFinalRound])

  return {
    phase,
    loading,
    statusMessage,
    error,
    qualification,
    finalRound,
    stats,
    lastDirection,
    qualificationFeedback,
    finalFeedback,
    finalCountdown,
    startGame,
    submitQualificationGuess,
    submitFinalGuess,
    resetGame,
    beginFinalCountdown,
  }
}
